import { useState, useEffect } from 'react';
import { authFetch } from '../../utils';
import { API_URL } from '../../config';

const STATUS_COLORS = {
  completed: '#28a745',
  'in-progress': '#007bff',
  ringing: '#17a2b8',
  queued: '#6c757d',
  initiated: '#6c757d',
  busy: '#fd7e14',
  failed: '#dc3545',
  'no-answer': '#fd7e14',
  canceled: '#6c757d',
  failure: '#dc3545',
};

function formatDate(ts) {
  return new Date(ts).toLocaleString('es-ES');
}

function downloadCSV(calls, sms) {
  const rows = [
    ['Tipo', 'Destinatario', 'Teléfono', 'Mensaje', 'Estado', 'Fecha'],
    ...calls.map(c => ['Llamada', c.name, c.phoneNumber, c.content ?? '', c.status, formatDate(c.timestamp)]),
    ...sms.map(s => ['SMS', s.name, s.phoneNumber, s.content ?? '', s.result, formatDate(s.timestamp)]),
  ];
  const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `notificaciones_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function NotificationsLog() {
  const [calls, setCalls] = useState([]);
  const [sms, setSms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    Promise.all([
      authFetch(`${API_URL}phonecall/logs?days=7`).then(r => r.json()),
      authFetch(`${API_URL}sms/logs?days=7`).then(r => r.json()),
    ]).then(([c, s]) => {
      setCalls(Array.isArray(c) ? c : []);
      setSms(Array.isArray(s) ? s : []);
    }).finally(() => setLoading(false));
  }, []);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const [c, s] = await Promise.all([
        authFetch(`${API_URL}phonecall/logs?days=90`).then(r => r.json()),
        authFetch(`${API_URL}sms/logs?days=90`).then(r => r.json()),
      ]);
      downloadCSV(Array.isArray(c) ? c : [], Array.isArray(s) ? s : []);
    } finally {
      setDownloading(false);
    }
  };

  const thStyle = { padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '13px', color: '#495057' };
  const tdStyle = { padding: '8px 12px', borderBottom: '1px solid #f0f0f0', fontSize: '13px' };
  const tableStyle = { width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff', borderRadius: '6px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' };

  if (loading) return <p style={{ color: '#6c757d' }}>Cargando registros...</p>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0 }}>Registros de notificaciones (últimos 7 días)</h2>
        <button
          onClick={handleDownload}
          disabled={downloading}
          style={{ backgroundColor: '#28a745', color: '#fff', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: downloading ? 'not-allowed' : 'pointer', opacity: downloading ? 0.7 : 1 }}
        >
          {downloading ? 'Generando...' : '⬇ Descargar últimos 3 meses (CSV)'}
        </button>
      </div>

      <h3 style={{ marginBottom: '0.5rem' }}>📞 Llamadas ({calls.length})</h3>
      {calls.length === 0 ? (
        <p style={{ color: '#6c757d', marginBottom: '2rem' }}>Sin llamadas en los últimos 7 días.</p>
      ) : (
        <div style={{ overflowX: 'auto', marginBottom: '2rem' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Destinatario</th>
                <th style={thStyle}>Teléfono</th>
                <th style={thStyle}>Mensaje</th>
                <th style={thStyle}>Estado</th>
                <th style={thStyle}>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {calls.map(c => (
                <tr key={c._id}>
                  <td style={tdStyle}>{c.name}</td>
                  <td style={tdStyle}>{c.phoneNumber}</td>
                  <td style={{ ...tdStyle, maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.content}>{c.content}</td>
                  <td style={tdStyle}>
                    <span style={{ backgroundColor: STATUS_COLORS[c.status] ?? '#6c757d', color: '#fff', padding: '2px 8px', borderRadius: '12px', fontSize: '12px' }}>
                      {c.status}
                    </span>
                  </td>
                  <td style={tdStyle}>{formatDate(c.timestamp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 style={{ marginBottom: '0.5rem' }}>💬 SMS ({sms.length})</h3>
      {sms.length === 0 ? (
        <p style={{ color: '#6c757d' }}>Sin SMS en los últimos 7 días.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Destinatario</th>
                <th style={thStyle}>Teléfono</th>
                <th style={thStyle}>Mensaje</th>
                <th style={thStyle}>Resultado</th>
                <th style={thStyle}>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {sms.map(s => (
                <tr key={s._id}>
                  <td style={tdStyle}>{s.name}</td>
                  <td style={tdStyle}>{s.phoneNumber}</td>
                  <td style={{ ...tdStyle, maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.content}>{s.content}</td>
                  <td style={tdStyle}>
                    <span style={{ backgroundColor: s.result === 'success' ? '#28a745' : '#dc3545', color: '#fff', padding: '2px 8px', borderRadius: '12px', fontSize: '12px' }}>
                      {s.result === 'success' ? 'Enviado' : 'Fallo'}
                    </span>
                  </td>
                  <td style={tdStyle}>{formatDate(s.timestamp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
